import { Button, Icon, IconButton } from "@wato/ui";
import { useEffect, useMemo, useState } from "react";
import { useI18n } from "@wato/i18n/react";
import { HelpArticleLink } from "../../components/HelpArticleLink";
import { useApi } from "../../api/context";
import {
  createBrowserPushPort,
  enablePush,
  localPushStore,
  PUSH_HINT_DISMISSED_KEY,
  type BrowserPushPort,
  type PushStore,
} from "./push";

const ENDPOINT_KEY = "wato.push.endpoint";

/** FEAT-03: "Notifications on this device" — enable/disable + test, shown in the user menu. */
export function DeviceNotifications({
  workspaceId,
  port,
  store,
}: {
  workspaceId: string;
  port?: BrowserPushPort;
  store?: PushStore;
}) {
  const api = useApi();
  const { t } = useI18n();
  const resolvedPort = useMemo(() => port ?? createBrowserPushPort(), [port]);
  const resolvedStore = useMemo(() => store ?? localPushStore(), [store]);
  /** undefined = still loading; null = the server has no VAPID key (push not switched on). */
  const [key, setKey] = useState<string | null | undefined>(undefined);
  const [endpoint, setEndpoint] = useState<string | null>(
    () => resolvedStore.get(ENDPOINT_KEY) || null,
  );
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  /** The informational "not switched on / not supported" notes can be hidden for good (per browser). */
  const [hintHidden, setHintHidden] = useState<boolean>(
    () => resolvedStore.get(PUSH_HINT_DISMISSED_KEY) === "1",
  );
  function hideHint() {
    resolvedStore.set(PUSH_HINT_DISMISSED_KEY, "1");
    setHintHidden(true);
  }

  useEffect(() => {
    let live = true;
    api
      .pushConfig()
      .then((c) => live && setKey(c.publicKey))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [api]);

  const supported = resolvedPort.supported();
  const denied = supported && resolvedPort.permission() === "denied";

  async function enable() {
    setBusy(true);
    setMessage(null);
    try {
      let registered: string | null = null;
      const result = await enablePush({
        port: resolvedPort,
        store: resolvedStore,
        vapidPublicKey: key ?? "",
        register: async (sub) => {
          await api.pushSubscribe(workspaceId, sub);
          registered = sub.endpoint;
        },
      });
      if (result.status === "subscribed" && registered) {
        resolvedStore.set(ENDPOINT_KEY, registered);
        setEndpoint(registered);
      } else if (result.status === "denied") setMessage(t("pwa.device.denied"));
      else setMessage(t("pwa.push.unsupported"));
    } catch {
      setMessage(t("pwa.device.error"));
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    if (!endpoint) return;
    setBusy(true);
    setMessage(null);
    try {
      await api.pushUnsubscribe(workspaceId, endpoint);
      resolvedStore.set(ENDPOINT_KEY, "");
      setEndpoint(null);
    } catch {
      setMessage(t("pwa.device.error"));
    } finally {
      setBusy(false);
    }
  }

  async function sendTest() {
    setBusy(true);
    setMessage(null);
    try {
      const { delivered } = await api.pushTest(workspaceId);
      setMessage(delivered > 0 ? t("pwa.device.testSent") : t("pwa.device.testNone"));
    } catch {
      setMessage(t("pwa.device.error"));
    } finally {
      setBusy(false);
    }
  }

  const infoOnly = key !== undefined && (!supported || key === null);
  if (infoOnly && hintHidden) return null;

  return (
    <section aria-labelledby="device-notif-title" className="border-t border-line px-3 py-2">
      <h3 id="device-notif-title" className="pb-1 text-xs font-semibold text-fg-muted">
        {t("pwa.device.title")}
      </h3>
      {key === undefined ? null : !supported ? (
        <p role="status" className="text-sm text-fg-muted">
          {t("pwa.push.unsupported")} {t("pwa.device.installHint")}
        </p>
      ) : key === null ? (
        <p role="status" data-testid="push-server-off" className="text-sm text-fg-muted">
          {t("pwa.device.serverOff")}
        </p>
      ) : denied ? (
        <p role="alert" className="text-sm text-fg-muted">
          {t("pwa.device.denied")} <HelpArticleLink slug="browser-notifications" />
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {endpoint ? (
            <>
              <IconButton
                variant="secondary"
                label={t("pwa.push.disable")}
                icon={<Icon name="pause" />}
                disabled={busy}
                onClick={() => void disable()}
              />
              <IconButton
                variant="secondary"
                label={t("pwa.device.test")}
                icon={<Icon name="bell" />}
                disabled={busy}
                onClick={() => void sendTest()}
              />
            </>
          ) : (
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => void enable()}
              className="min-h-11"
            >
              {t("pwa.push.enable")}
            </Button>
          )}
        </div>
      )}
      {infoOnly ? (
        <button
          type="button"
          data-testid="push-hint-hide"
          onClick={hideHint}
          className="mt-1 min-h-11 text-sm text-action-primary underline"
        >
          {t("pwa.device.hideHint")}
        </button>
      ) : null}
      {message ? (
        <p role="status" className="pt-1 text-sm text-fg-muted">
          {message}
        </p>
      ) : null}
    </section>
  );
}
